"use client";

import React, { useState } from "react";
import { motion } from "framer-motion";
import { Section } from "@/components/section";
import { Badge } from "@/components/ui/badge";
import { ShoppingCart, Package } from "lucide-react";
import { StorefrontComponent } from "../../../components/storefront-component";
import ShoppingCartComponent from "@/components/ui/shopping-cart";
import ProductDetails from "@/components/ui/product-details";
import { MOCK_REPOSITORY_PRODUCTS } from "@/lib/polar";

export default function StorePage() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-background/95">
      {/* Hero Section */}
      <Section className="relative overflow-hidden py-20 lg:py-24">
        <div className="absolute inset-0 z-0 opacity-[0.03] bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] [background-size:16px_16px]"></div>
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <Badge 
              variant="outline" 
              className="mb-4 px-3 py-1 text-sm font-medium shadow-sm transition-colors hover:bg-accent"
            >
              Kled.io Store
            </Badge>
            <h1 className="mb-6 text-4xl font-bold tracking-tight text-foreground sm:text-5xl md:text-6xl">
              Premium <span className="bg-clip-text text-transparent bg-gradient-to-r from-emerald-500 to-blue-500">Software</span> Products
            </h1>
            <p className="mx-auto mb-8 max-w-2xl text-lg text-muted-foreground">
              Browse our collection of high-quality, enterprise-ready software components and tools.
            </p>
          </div>
        </div>
      </Section>

      {/* Storefront Component */}
      <Section className="py-12">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8 flex items-center space-x-2">
            <Package className="h-5 w-5 text-emerald-500" />
            <h2 className="text-2xl font-semibold text-foreground">Featured Products</h2>
          </div>
          
          <div className="mb-12">
            <ShoppingCartComponent />
          </div>
          
          <StorefrontComponent 
            products={MOCK_REPOSITORY_PRODUCTS.map(repo => {
              const colors = ['#10b981', '#3b82f6', '#8b5cf6']; // Default color options
              return {
                ...repo,
                image: repo.imageSrc || '/images/repositories/placeholder.png',
                category: repo.repositoryName.split('-').join(' '),
                color: colors[0], // Set the default color
                colors: colors, 
                sizes: ['Standard', 'Enterprise'], // License types as "sizes"
                inStock: true,
                featured: true,
                rating: 4.5,
              };
            })} 
            currency="$"
          />
        </div>
      </Section>

      {/* Security Section */}
      <Section className="py-16 bg-muted/30">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <h2 className="text-2xl font-semibold text-foreground mb-4">Secure Checkout Process</h2>
            <p className="text-muted-foreground mb-8">
              All purchases are processed securely through Polar.sh, our trusted payment provider.
              Your transaction is protected with enterprise-grade encryption.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
              <div className="bg-card rounded-xl p-6 border border-border/50 shadow-sm">
                <div className="h-10 w-10 bg-emerald-100 dark:bg-emerald-900/30 rounded-full flex items-center justify-center mb-4 mx-auto">
                  <Package className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                </div>
                <h3 className="text-lg font-medium mb-2 text-foreground">Quality Software</h3>
                <p className="text-sm text-muted-foreground">
                  Our products are built with the highest standards and regularly maintained.
                </p>
              </div>
              
              <div className="bg-card rounded-xl p-6 border border-border/50 shadow-sm">
                <div className="h-10 w-10 bg-emerald-100 dark:bg-emerald-900/30 rounded-full flex items-center justify-center mb-4 mx-auto">
                  <ShoppingCart className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                </div>
                <h3 className="text-lg font-medium mb-2 text-foreground">Secure Payments</h3>
                <p className="text-sm text-muted-foreground">
                  All payments are processed securely with enterprise-grade encryption.
                </p>
              </div>
              
              <div className="bg-card rounded-xl p-6 border border-border/50 shadow-sm">
                <div className="h-10 w-10 bg-emerald-100 dark:bg-emerald-900/30 rounded-full flex items-center justify-center mb-4 mx-auto">
                  <svg className="h-5 w-5 text-emerald-600 dark:text-emerald-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 6 7 17l-5-5" />
                    <path d="m22 10-7.5 7.5L13 16" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium mb-2 text-foreground">Full Support</h3>
                <p className="text-sm text-muted-foreground">
                  One year of dedicated support included with all purchases.
                </p>
              </div>
            </div>
          </div>
        </div>
      </Section>
    </div>
  );
}
